import clsx from 'clsx';
import merge from 'lodash/merge';
import min from 'lodash/min';
import React from 'react';

import CustomResizeObserver from 'lib/ResizeObserver';
import { getVideoPreviewUrl, getVideoUrl } from 'lib/cloudinary';

import IconButton from '@material-ui/core/IconButton';
import PlayCircleOutline from '@material-ui/icons/PlayCircleOutline';

import { useStyles } from './styles';

type PlayerOptions = {
	controls: boolean;
	autoPlay: boolean;
	muted: boolean;
	fullScreen: boolean;
	preview: boolean;
	loop: boolean;
	autoplayOnScroll: boolean;
	showPlayButton: boolean;
};

export type ComponentProps = {
	id: string;
	videoId: string;
	cloudinaryVersion: string;
	config?: Partial<PlayerOptions>;
	overlay?: React.ReactElement;
	onPlay?: React.EffectCallback;
	objectFit?: React.CSSProperties['objectFit'];
};

const VideoPlayer = (props: ComponentProps) => {
	const { videoId, id, config, overlay, cloudinaryVersion, onPlay, objectFit = 'cover' } = props;

	const videoRef = React.useRef<HTMLVideoElement>();
	const previewVideoRef = React.useRef<HTMLVideoElement>();
	const [isVideoPlaying, setVideoPlaying] = React.useState(false);
	const [isPreviewPlaying, setPreviewPlaying] = React.useState(false);
	const [isFullScreen, setFullScreen] = React.useState(false);
	const [videoPlayed, setVideoPlayed] = React.useState(false);
	const [scrollActive, setScrollActive] = React.useState(true);
	const [videoPlayFailed, setVideoPlayFailed] = React.useState(false);
	const classes = useStyles({ objectFit });

	const [videoWidth, setVideoWidth] = React.useState(0);
	const videoContainerRef = React.useRef<HTMLDivElement>();

	const options = React.useMemo(() => {
		const defaultOptions: PlayerOptions = {
			controls: false,
			autoPlay: false,
			muted: true,
			fullScreen: true,
			preview: true,
			loop: false,
			autoplayOnScroll: false,
			showPlayButton: true
		};
		return merge(defaultOptions, config);
	}, [config]);

	const canPlayPreview = React.useMemo(() => options.preview && !videoPlayed, [videoPlayed, options.preview]);
	const playOnScrollEnabled = options.autoplayOnScroll && scrollActive;
	const canShowControls = isFullScreen || (options.controls && !playOnScrollEnabled) || videoPlayFailed;
	const showCover = !canShowControls && ((!videoPlayed && !isPreviewPlaying) || (videoPlayed && !isVideoPlaying && playOnScrollEnabled));
	const canShowPlayButton = options.showPlayButton && !options.autoPlay && !isVideoPlaying && !isPreviewPlaying && !canShowControls;

	/* PREVIEW ACTIONS AND USE EFFECTS */
	React.useEffect(() => {
		if (previewVideoRef.current) {
			const { current: videoPreview } = previewVideoRef;
			videoPreview.onplaying = () => setPreviewPlaying(true);
			videoPreview.onpause = () => setPreviewPlaying(false);
			videoPreview.onended = ev => {
				setPreviewPlaying(false);
			};
		}
	}, []);

	const playPreview = React.useCallback(async () => {
		if (!canPlayPreview) return;
		if (onPlay) onPlay();
		if (!isPreviewPlaying) {
			previewVideoRef.current.currentTime = 0;
			previewVideoRef.current.play();
		}
	}, [canPlayPreview, isPreviewPlaying, onPlay]);

	const stopPreview = React.useCallback(() => {
		if (!canPlayPreview) return;
		if (isPreviewPlaying) previewVideoRef.current.pause();
	}, [canPlayPreview, isPreviewPlaying]);

	/* RESIZE OBSERVER FOR SETTING PLAY BUTTON FONT SIZE DYNAMICALLY */
	React.useEffect(() => {
		if (videoContainerRef.current) {
			const { current: videoContainer } = videoContainerRef;
			// @ts-ignore
			const observer = new CustomResizeObserver(() => {
				setVideoWidth(videoContainer.offsetWidth);
			});
			observer.observe(videoContainer);
			return () => observer.unobserve(videoContainer);
		}
	}, [videoContainerRef]);

	const handleFullScreenChange = React.useCallback(
		(fullscreenElement, video) => {
			if (!fullscreenElement) videoContainerRef.current ? videoContainerRef.current.append(video) : document.body.removeChild(video);
			setFullScreen(!!fullscreenElement);
			if (!fullscreenElement) {
				video.pause();
				video.options = options;
			} else video.play();
		},
		[options]
	);

	/* VIDEO EVENT LISTENERS */
	React.useEffect(() => {
		const { current: video } = videoRef;
		video.onplaying = ev => {
			setVideoPlayed(true);
			setVideoPlaying(true);
			setPreviewPlaying(false);
		};
		video.onpause = ev => {
			setVideoPlaying(false);
		};
		video.onended = ev => {
			setVideoPlaying(false);
		};
		video.onload = ev => {
			setVideoPlaying(false);
		};
		video.addEventListener('fullscreenchange', function (e) {
			handleFullScreenChange(document.fullscreenElement, video);
		});
		video.addEventListener('webkitfullscreenchange', function () {
			//We should put the video back in its original container when exiting full screen
			//@ts-ignore
			handleFullScreenChange(document.webkitIsFullScreen, video);
		});
		video.addEventListener('mozfullscreenchange', function () {
			//@ts-ignore
			handleFullScreenChange(document.mozFullScreen, video);
		});
		video.addEventListener('msfullscreenchange', function () {
			//@ts-ignore
			handleFullScreenChange(document.msFullscreenElement, video);
		});
	}, [handleFullScreenChange]);

	const openFullScreen = React.useCallback(async () => {
		const { current: video } = videoRef;
		if (video.requestFullscreen) {
			document.body.appendChild(video);
			await video.requestFullscreen();
		}
		//@ts-ignore
		else if (video.mozRequestFullScreen) {
			document.body.appendChild(video);
			//@ts-ignore
			await video.mozRequestFullScreen();
		}
		//@ts-ignore
		else if (video.msRequestFullscreen) {
			document.body.appendChild(video);
			//@ts-ignore
			await video.msRequestFullscreen();
		}
		//@ts-ignore
		else if (video.webkitRequestFullscreen) {
			document.body.appendChild(video);
			//@ts-ignore
			await video.webkitRequestFullscreen();
		}
	}, []);

	const playVideo = React.useCallback(async () => {
		const { current: video } = videoRef;
		if (!isVideoPlaying) {
			try {
				if (onPlay) onPlay();
				await video.play();
			} catch (err) {
				setVideoPlayed(true);
				setVideoPlayFailed(true);
				setScrollActive(false);
				console.log(err);
			}
		}
	}, [isVideoPlaying, onPlay]);

	const stopVideo = React.useCallback(() => {
		const { current: video } = videoRef;
		if (isVideoPlaying) video.pause();
	}, [isVideoPlaying]);

	/* PLAY VIDEO ON SCROLL ON MOBILE VERSION */
	React.useEffect(() => {
		if (videoContainerRef.current && playOnScrollEnabled) {
			const { current: videoContainer } = videoContainerRef;
			const observer = new IntersectionObserver(
				entries => {
					const { isIntersecting } = entries[0];
					if (isIntersecting) playVideo();
					else stopVideo();
				},
				{ threshold: 0.6, rootMargin: '-20% 0px -20% 0px' }
			);
			observer.observe(videoContainer);
			return () => observer.unobserve(videoContainer);
		}
	}, [playOnScrollEnabled, playVideo, stopVideo]);

	const videoClickHandler = React.useCallback(
		async (ev: React.MouseEvent) => {
			const { current: video } = videoRef;
			// We don't handle this action if someone else already has control over the video
			if (canShowControls || options.autoPlay) return;
			if (isVideoPlaying && !playOnScrollEnabled) return video.pause();
			if (options.fullScreen) await openFullScreen();
			if (!videoPlayed) video.muted = options.muted;
			await playVideo();
			setScrollActive(false);
		},
		[
			canShowControls,
			isVideoPlaying,
			openFullScreen,
			options.autoPlay,
			options.fullScreen,
			options.muted,
			playOnScrollEnabled,
			videoPlayed,
			playVideo
		]
	);

	const player = React.useMemo(() => {
		const optimizedUrl = getVideoUrl(videoId, { transformation: 'videoFull', version: cloudinaryVersion, format: 'webm' });
		const url = getVideoUrl(videoId);

		return (
			<video
				className={clsx(classes.videoPlayer, { [classes.videoPlayerHidden]: canPlayPreview, [classes.videoPlayerFullScreen]: isFullScreen })}
				ref={videoRef}
				autoPlay={options.autoPlay}
				controls={canShowControls}
				controlsList="nodownload"
				muted={options.muted}
				loop={options.loop}
				playsInline
			>
				<source src={optimizedUrl}></source>
				<source src={url}></source>
			</video>
		);
	}, [videoId, classes, canPlayPreview, isFullScreen, options, canShowControls, cloudinaryVersion]);

	const previewPlayer = React.useMemo(() => {
		if (!canPlayPreview) return null;

		const optimizedUrl = getVideoUrl(videoId, { transformation: 'videoPreview', version: cloudinaryVersion, format: 'webm' });
		const url = getVideoUrl(videoId);

		return (
			<video
				className={clsx(classes.videoPlayer, { [classes.videoPlayerHidden]: !canPlayPreview })}
				ref={previewVideoRef}
				loop={true}
				controls={false}
				muted={true}
			>
				<source src={optimizedUrl}></source>
				<source src={url}></source>
			</video>
		);
	}, [canPlayPreview, videoId, classes, cloudinaryVersion]);

	const playButton = React.useMemo(() => {
		const iconFontSize = min([videoWidth * 0.9, 100]) + 'px';
		return (
			<div className={classes.videoItemButtonContainer}>
				<IconButton id={`videoPlayerPlayButton-${id}`} className={clsx(classes.videoPlayButton, 'video-play-icon-button')}>
					<PlayCircleOutline className={classes.videoItemIcon} style={{ fontSize: iconFontSize }} />
				</IconButton>
			</div>
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [videoId, videoWidth]);

	const videoThumbnail = React.useMemo(
		() => (
			<div
				id={`videoPlayerThumbnail-${id}`}
				className={clsx(classes.videoThumbnailContainer, {
					[classes.videoThumbnailHidden]: !showCover
				})}
			>
				<img className={classes.videoThumbnail} src={getVideoPreviewUrl(videoId, { version: cloudinaryVersion })} alt="cover" />
			</div>
		),
		[id, classes, showCover, videoId, cloudinaryVersion]
	);

	return (
		<div
			id={`videoPlayer-${id}`}
			className={clsx(classes.videoPlayerContainer, {
				[classes.videoPointerDisabled]: options.autoPlay && !options.controls
			})}
			onMouseEnter={playPreview}
			onMouseLeave={stopPreview}
			onClick={videoClickHandler}
			ref={videoContainerRef}
		>
			{overlay || null}
			{player}
			{previewPlayer}
			<div className={clsx(classes.videoCover, { [classes.coverDisabled]: !showCover })}>
				{videoThumbnail}
				{canShowPlayButton ? playButton : null}
			</div>
		</div>
	);
};

export default React.memo(VideoPlayer);
